import { useOrgBillingTransferCandidatesQuery } from "@api/orgBillingTransferRequest.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { FC, PropsWithChildren } from "react";
import actionsStyles from "../ClientActions.module.scss";
import { ClientDetailsRow } from "../views/clientViewConfig";
import { NoOtherClientAdminsNotice } from "./NoOtherClientAdminsNotice";
import { RequestBillingTransferToClientForm } from "./RequestBillingTransferToClientForm";

interface RequestBillingTransferToClientModalProps {
    client: ClientDetailsRow;
    organizationId: string;
    onClose: () => void;
}

const CloseOnlyContent: FC<PropsWithChildren<{ onClose: () => void }>> = ({
    children,
    onClose,
}) => (
    <VStack gap={16}>
        {children}
        <HStack as="footer" justify="end">
            <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                size="xl"
            >
                Close
            </Button>
        </HStack>
    </VStack>
);

export const RequestBillingTransferToClientModal: FC<
    RequestBillingTransferToClientModalProps
> = ({ client, organizationId, onClose }) => {
    const candidatesQuery = useOrgBillingTransferCandidatesQuery({
        organizationId,
        clientId: client.id,
    });
    const candidates = candidatesQuery.data;

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Transfer billing ownership
            </StandardModalHeader>
            <StandardModalBody className={actionsStyles.modalBody}>
                <LoaderOnInitialLoading queryResult={candidatesQuery}>
                    {candidates && candidates.candidates.length > 0 ? (
                        <RequestBillingTransferToClientForm
                            clientId={client.id}
                            organizationId={organizationId}
                            candidates={candidates}
                            onClose={onClose}
                        />
                    ) : (
                        <CloseOnlyContent onClose={onClose}>
                            <NoOtherClientAdminsNotice />
                        </CloseOnlyContent>
                    )}
                </LoaderOnInitialLoading>
                {candidatesQuery.isError && (
                    <CloseOnlyContent onClose={onClose}>
                        <p>Failed to load billing transfer options.</p>
                    </CloseOnlyContent>
                )}
            </StandardModalBody>
        </>
    );
};
